import xs from "xstream/index";
import * as ActionTypes from "../actions/types";
import * as actions from "../actions/index";
import * as Requests from "./requests";
import sampleCombine from "xstream/extra/sampleCombine";
import delay from "xstream/extra/delay";
import { push } from "../../utils/navigation";
import dependencies from "../dependencies";
import { LOGOUT_SUCCESS } from "../actions/types";
import { FETCH_LOGIN_STATUS_SUCCESS } from "../actions/types";
import {
  getUserIdFromState,
  createBackendRequest,
} from "../../mappers/backendMappers";
import v4 from "uuid/v4";

const { newBaseUrl, generalHeader, frontEndUrl, oAuthRedirectUrl } =
  dependencies;

export function requestFetchToken(sources) {
  const request$ = sources.ACTION.filter(
    (action) => action.type === ActionTypes.FETCH_TOKEN
  ).map(() => ({
    url: newBaseUrl + "/csrftoken/token",
    category: Requests.REQUEST_FETCH_TOKEN,
    method: "GET",
    headers: generalHeader(null, true),
    withCredentials: true,
  }));
  const httpResponse$ = sources.HTTP.select(Requests.REQUEST_FETCH_TOKEN)
    .map((response) => response.replaceError((err) => xs.of(err)))
    .flatten()
    .map((response) =>
      response.status === 200
        ? actions.fetchTokenSuccess(response.body.token)
        : actions.fetchTokenFail(response.text)
    );
  return {
    ACTION: httpResponse$,
    HTTP: request$,
  };
}

export function requestOauthParameters(sources) {
  const state$ = sources.STATE;
  const userId$ = state$.map((state) => getUserIdFromState(state));

  const request$ = sources.ACTION.filter(
    (action) => action.type === ActionTypes.FETCH_OAUTH_PARAMETERS
  )
    .compose(sampleCombine(userId$))
    .map(([action, userId]) => {
      // Create request body with lowercase field names as per API spec
      const requestBody = {
        header: {
          id: v4(),
          application: "03FC0B90-DFAD-11EE-8D86-0800200C9A66",
          bank: "NBG",
          userId: userId || "00000000-0000-0000-0000-000000000000",
        },
        payload: {
          provider: "generic",
        },
      };

      return {
        url: newBaseUrl + "/OpenIdConnect/authorization-info/generic",
        category: Requests.REQUEST_OAUTH_PARAMETERS,
        method: "POST",
        headers: generalHeader(null, true),
        withCredentials: true,
        send: requestBody,
      };
    });

  const httpResponse$ = sources.HTTP.select(Requests.REQUEST_OAUTH_PARAMETERS)
    .map((response) => response.replaceError((err) => xs.of(err)))
    .flatten()
    .map((response) =>
      response.status === 200
        ? actions.fetchOauthParametersSuccess(response.body.payload)
        : actions.fetchOauthParametersFailed(response)
    );
  return {
    ACTION: httpResponse$,
    HTTP: request$,
  };
}

export function requestLogin(sources) {
  const state$ = sources.STATE;
  const userId$ = state$.map((state) => getUserIdFromState(state));

  const request$ = sources.ACTION.filter(
    (action) => action.type === ActionTypes.LOGIN
  )
    .compose(sampleCombine(userId$))
    .map(([action, userId]) => {
      const { url } = action.payload;
      const finalUrl = new URL(url);

      // Extract OAuth callback parameters from URL
      const urlParams = new URLSearchParams(finalUrl.search);
      const code = urlParams.get("code");
      const state = urlParams.get("state");
      const scope = urlParams.get("scope");
      const sessionState = urlParams.get("session_state");
      const iss = urlParams.get("iss");

      // Build request body with OAuth callback parameters
      const requestBody = {
        header: {
          id: v4(),
          application: "03FC0B90-DFAD-11EE-8D86-0800200C9A66",
          bank: "NBG",
          userId: userId || "00000000-0000-0000-0000-000000000000",
        },
        payload: {
          code: code || "",
          state: state || "",
          scope: scope || "",
          sessionState: sessionState || "",
          iss: iss || "",
          redirectUri: oAuthRedirectUrl,
        },
      };

      return {
        url: newBaseUrl + "/OpenIdConnect/generic",
        category: Requests.REQUEST_LOGIN,
        method: "POST",
        headers: generalHeader(null, true),
        withCredentials: true,
        send: requestBody,
      };
    });

  const httpResponse$ = sources.HTTP.select(Requests.REQUEST_LOGIN)
    .map((response) => response.replaceError((err) => xs.of(err)))
    .flatten()
    .map((response) =>
      response.status === 200
        ? actions.loginSuccess(response.body.payload)
        : actions.loginFailed(response)
    );
  return {
    ACTION: httpResponse$,
    HTTP: request$,
  };
}

export function requestLoginStatus(sources) {
  const request$ = sources.ACTION.filter(
    (action) => action.type === ActionTypes.FETCH_LOGIN_STATUS
  ).map((action) => ({
    url: newBaseUrl + "/user/login_status",
    category: Requests.REQUEST_LOGIN_STATUS,
    method: "GET",
    headers: generalHeader(null, true),
    withCredentials: true,
  }));

  let httpResponse$ = sources.HTTP.select(Requests.REQUEST_LOGIN_STATUS)
    .map((response) => response.replaceError((err) => xs.of(err)))
    .flatten()
    .map((response) => {
      if (response.status !== 200) {
        return xs.of(actions.fetchLoginStatusFailed(response));
      }

      const isAuthenticated = !!response.body.isAuthenticated;
      const userId = response.body.userId;
      const statusAction = actions.fetchLoginStatusSuccess(
        isAuthenticated ? "1" : "0"
      );

      // An existing cookie session means the user is already authenticated
      // (e.g. page refresh), but no interactive login POST happens in this
      // case, so trigger the same user-details hydration that a fresh login
      // would (which in turn populates completed stories via STORE_USER_DETAILS).
      return isAuthenticated && userId
        ? xs.of(statusAction, actions.fetchUserDetails(userId))
        : xs.of(statusAction);
    })
    .flatten();

  return {
    ACTION: httpResponse$,
    HTTP: request$,
  };
}

export function requestFetchUserDetails(sources) {
  const state$ = sources.STATE;
  const applicationUser$ = state$.map((state) => state.applicationUser);

  const request$ = sources.ACTION.filter(
    (action) => action.type === ActionTypes.FETCH_USER_DETAILS
  )
    .compose(sampleCombine(applicationUser$))
    .map(([action, applicationUser]) => {
      // The action.payload contains the user ID
      const userId = action.payload;
      // Use email from state or from recent login
      const userEmail = applicationUser?.mail || applicationUser?.email || "";

      // Build request body for new endpoint
      const requestBody = {
        header: {
          id: v4(),
          application: "03FC0B90-DFAD-11EE-8D86-0800200C9A66",
          bank: "NBG",
          userId: userId || "00000000-0000-0000-0000-000000000000",
        },
        payload: {
          id: userId,
          email: userEmail,
        },
      };

      return {
        url: newBaseUrl + "/User/get",
        category: Requests.REQUEST_FETCH_USER_DETAILS,
        method: "POST",
        headers: generalHeader(null, true),
        withCredentials: true,
        send: requestBody,
      };
    });

  const httpResponse$ = sources.HTTP.select(Requests.REQUEST_FETCH_USER_DETAILS)
    .map((response) => response.replaceError((err) => xs.of(err)))
    .flatten()
    .map((response) =>
      response.status === 200
        ? actions.storeUserDetails(response.body.payload)
        : actions.fetchUserDetailsFailed(response)
    );

  return {
    ACTION: httpResponse$,
    HTTP: request$,
  };
}

export function updateCompletedStories(sources) {
  const state$ = sources.STATE;
  const applicationUser$ = state$.map((state) => state.applicationUser);

  const request$ = sources.ACTION.filter(
    (action) => action.type === ActionTypes.UPDATE_COMPLETED_STORIES
  )
    .compose(sampleCombine(applicationUser$))
    .filter(([action, applicationUser]) => !!applicationUser?.uuid)
    .map(([action, applicationUser]) => ({
      url: newBaseUrl + "/UserStoryCompletion/complete",
      category: Requests.UPDATE_COMPLETED_STORIES,
      method: "POST",
      headers: generalHeader(applicationUser?.token, true),
      withCredentials: true,
      send: createBackendRequest(
        { StoryId: action.payload },
        applicationUser.uuid
      ),
    }));

  const httpResponse$ = sources.HTTP.select(Requests.UPDATE_COMPLETED_STORIES)
    .map((response) => response.replaceError((err) => xs.of(err)))
    .flatten()
    .filter((response) => response.status === 200)
    .map((response) => {
      const stories = response.body.payload || [];
      return actions.updateCompletedStoriesSuccess(stories);
    });

  return {
    ACTION: httpResponse$,
    HTTP: request$,
  };
}

export function requestFetchCompletedStories(sources) {
  const request$ = sources.ACTION.filter(
    (action) => action.type === ActionTypes.STORE_USER_DETAILS
  )
    .map((action) => {
      const userId = action.payload?.id;
      if (!userId) return null;
      return {
        url: newBaseUrl + "/UserStoryCompletion/list",
        category: Requests.FETCH_COMPLETED_STORIES,
        method: "POST",
        headers: generalHeader(null, true),
        withCredentials: true,
        send: createBackendRequest({}, userId),
      };
    })
    .filter((req) => req !== null);

  const httpResponse$ = sources.HTTP.select(Requests.FETCH_COMPLETED_STORIES)
    .map((response) => response.replaceError((err) => xs.of(err)))
    .flatten()
    .filter((response) => response.status === 200)
    .map((response) => {
      const stories = response.body.payload || [];
      return actions.updateCompletedStoriesSuccess(stories);
    });

  return {
    ACTION: httpResponse$,
    HTTP: request$,
  };
}

export function requestLogout(sources) {
  const state$ = sources.STATE;
  const csrfToken$ = state$.map((state) => state.applicationUser?.token);
  const request$ = sources.ACTION.filter(
    (action) => action.type === ActionTypes.LOGOUT
  )
    .compose(sampleCombine(csrfToken$))
    .map(([action, csrfToken]) => ({
      // eslint-disable-line
      url: newBaseUrl + "/user/logout",
      category: Requests.REQUEST_LOGOUT,
      method: "GET",
      headers: generalHeader(csrfToken),
      withCredentials: true,
    }));

  const httpResponse$ = sources.HTTP.select(Requests.REQUEST_LOGOUT)
    .map((response) => response.replaceError((err) => xs.of(err)))
    .flatten()
    .map((response) =>
      response.status === 200
        ? actions.logoutSuccess(response)
        : actions.logoutFail(response)
    );

  return {
    ACTION: httpResponse$,
    HTTP: request$,
  };
}

export function redirectAfterSuccessfulLogin(sources) {
  const state$ = sources.STATE;
  const redirects$ = state$.map((state) => state.applicationUser?.redirects);

  // We need to capture the original request to get the state parameter
  const requestWithResponse$ = sources.ACTION.filter(
    (action) => action.type === ActionTypes.LOGIN
  )
    .map((action) => {
      const { url } = action.payload;
      const finalUrl = new URL(url);
      const urlParams = new URLSearchParams(finalUrl.search);
      return urlParams.get("state");
    })
    .compose(
      sampleCombine(
        sources.HTTP.select(Requests.REQUEST_LOGIN)
          .map((response) => response.replaceError((err) => xs.of(err)))
          .flatten()
      )
    )
    .filter(([state, response]) => response.status === 200)
    .compose(sampleCombine(redirects$))
    .compose(delay(0))
    .map(([stateAndResponse, redirects]) => {
      const [state] = stateAndResponse;
      const returnUrl = redirects ? redirects[state] : undefined;

      // If no returnUrl or it's invalid, redirect to home
      if (!returnUrl || returnUrl === "undefined" || returnUrl === "null") {
        return push("/");
      }

      // Clean and validate the return URL
      const cleanUrl = returnUrl.replace(frontEndUrl, "");

      // If the cleaned URL is empty or just "/", go to home
      if (!cleanUrl || cleanUrl === "/") {
        return push("/");
      }

      // Ensure the URL starts with /
      const finalUrl = cleanUrl.startsWith("/") ? cleanUrl : "/" + cleanUrl;
      return push(finalUrl);
    });

  return {
    ACTION: requestWithResponse$,
  };
}

export function fetchUserDetailsAfterSuccessfulLogin(sources) {
  const httpResponse$ = sources.HTTP.select(Requests.REQUEST_LOGIN)
    .map((response) => response.replaceError((err) => xs.of(err)))
    .flatten()
    .filter((response) => response.status === 200)
    .map((response) => {
      // The new endpoint returns user details directly in the payload
      // We still fetch additional user details if needed
      const userId = response.body.payload?.user_id;
      return userId ? xs.of(actions.fetchUserDetails(userId)) : xs.empty();
    })
    .flatten();

  return {
    ACTION: httpResponse$,
  };
}

export function redirectAfterSuccessfulLogout(sources) {
  let httpResponse$ = sources.ACTION.filter(
    (action) => action.type === LOGOUT_SUCCESS
  ).map(() => push("/"));
  return {
    ACTION: httpResponse$,
  };
}

export function logoutAfterDeniedLogin(sources) {
  const state$ = sources.STATE;
  const isLoggedIn$ = state$.map((state) => state.applicationUser?.isLoggedIn);

  const httpResponse$ = sources.ACTION.filter(
    (action) => action.type === FETCH_LOGIN_STATUS_SUCCESS
  )
    .compose(sampleCombine(isLoggedIn$))
    .compose(delay(0))
    .filter(([action, isLoggedIn]) => isLoggedIn && action.payload === "0")
    .map(([action, isLoggedIn]) => actions.logout());

  return {
    ACTION: httpResponse$,
  };
}
